import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import _classNames from 'classnames';
import PropTypes from 'prop-types';
import withSizes from 'react-sizes';
import _get from 'lodash/get';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import ScrollTrigger from 'containers/TopicCenter/components/Lists/ScrollTrigger';
import ButtonBase from '@material-ui/core/ButtonBase';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import ArrowUp from '@material-ui/icons/ArrowDropUp';
import { makeStyles } from '@material-ui/core/styles';
import { getImageFile } from 'utils/stringUtils';
import Carousel from 'components/ResourcesCarousel';
import SwiperCarousel from 'components/SwiperCarousel';
import _has from 'lodash/has';
import ResourceItemSelector from 'components/ResourceItemSelector';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import ListViewAllCard from 'components/ListViewAllCard';
import { filterSelfReferencingResources } from 'containers/TopicCenter/utils';
import { getScoreComponentItemId } from 'components/SurveyResult/utils';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    [theme.breakpoints.up('768')]: {
      paddingLeft: props =>
        props.isVariantB ? '40px !important' : '0 !important',
      paddingRight: props =>
        props.isVariantB ? '40px !important' : '0 !important',
      marginLeft: -4,
      maxWidth: 'calc(100% + 8px) !important',
      width: 'calc(100% + 8px) !important',
    },
  },
  tabsWrapper: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    '> button:not(.small):first-child, & .cm-resources-carousel-item:first-child > button': {
      // borderRadius: '9px 0 0 9px',
      borderTopLeftRadius: 9,
      borderBottomLeftRadius: 9,
      marginLeft: 0,
      '& > div:first-child': {
        // borderRadius: '9px 0 0 9px',
        borderTopLeftRadius: 9,
        borderBottomLeftRadius: 9,
      },
    },
    '> button:not(.small):last-child, & .cm-resources-carousel-item:last-child > button': {
      // borderRadius: '0 9px 9px 0',
      borderTopRightRadius: 9,
      borderBottomRightRadius: 9,
      marginRight: 0,
      '& > span': {
        // borderRadius: '0 9px 9px 0',
        borderTopRightRadius: 9,
        borderBottomRightRadius: 9,
      },
    },
    [theme.breakpoints.down('sm')]: {
      '> button:not(.small):first-child, & .cm-resources-carousel-item:first-child > button': {
        borderRadius: 9,
        '& > div:first-child': {
          borderRadius: 9,
        },
      },
      '> button:not(.small):last-child, & .cm-resources-carousel-item:last-child > button': {
        borderRadius: 9,
        '& > span': {
          borderRadius: 9,
        },
      },
    },
  },
  tab: {
    display: 'flex',
    justifyContent: 'flex-start',
    flex: '1 1 0',
    textAlign: 'left',
    border: '1px solid #E0E0E0',
    height: props => (props.isDesktop ? 78 : 58),
    marginRight: 8,
    marginLeft: 8,
    fontFamily: props => (props.isVariantB ? 'Poppins' : 'PoppinsSemiBold'),
    backgroundColor: props => (props.isVariantB ? '#FFF' : undefined),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    width: '100%',
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
    '&.selected': {
      color: '#fff',
      backgroundColor: props =>
        props.isVariantB ? theme.palette.primary.main : undefined,
      '&:not(.small) > span': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    '&:hover:not(.selected)': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    maxWidth: props => (props.isTablet ? 285 : 350),
    '&:not(.small) > div:first-child': {
      minWidth: props => (props.isDesktop ? 76 : 40),
      height: '100%',
    },
    '&:not(.small) > span': {
      backgroundColor: '#fff',
    },
    '&.small': {
      maxWidth: 220,
      borderRadius: 8,
      backgroundColor: '#fff',
      '&.selected': {
        backgroundColor: theme.palette.primary.main,
      },
      '& > div:first-child': {
        marginLeft: 5,
        borderRadius: 8,
        minWidth: 40,
        height: 40,
      },
    },
  },
  mobileListTab: {
    height: 58,
    borderColor: 'transparent',
    '&.small': {
      borderRadius: 0,
      backgroundColor: 'transparent',
      '& > div:first-child': {
        borderRadius: 8,
        border: '1px solid #C5C5C5',
      },
      '& > span': {
        backgroundColor: 'transparent',
      },
    },
    '&:hover:not(.selected)': {
      borderColor: 'transparent',
      color: theme.palette.text.primary,
    },
  },
  tabImageWrapper: {
    overflow: 'hidden',
    '& > img': {
      height: '100%',
      maxWidth: '100%',
    },
  },
  tabLabel: {
    height: props => (props.isVariantB ? 'auto' : '100%'),
    display: props => (props.isVariantB ? 'block' : 'inline-flex'),
    alignItems: 'center',
    flexGrow: 1,
    padding: '0 14px',
    textAlign: props => (props.isVariantB ? 'center' : 'left'),
    [theme.breakpoints.down('sm')]: {
      height: () => '100%',
      display: () => 'inline-flex',
      justifyContent: props => (props.isVariantB ? 'center' : 'left'),
    },
  },
  tabLabelSelected: {
    [theme.breakpoints.down('sm')]: {
      padding: props => (props.isVariantB ? '0 40px' : '0 14px'),
    },
  },
  mobileSelectedTabArrow: {
    position: 'absolute',
    top: 'auto',
    bottom: 'auto',
    right: 8,
    color: '#FFFFFF',
    fontSize: '2.5rem',
  },
  popperContainer: {
    zIndex: 1,
    width: '100%',
  },
  mobilePaper: {
    borderRadius: '8px !important',
    border: '1px solid #E3E3E3',
    overflow: 'hidden',
  },
  mobileList: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  mobileListItem: {
    '&.Mui-selected,&.Mui-selected:hover': {
      backgroundColor: '#F5FBFF',
    },
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
  button: {
    display: 'block',
    width: '100%',
  },
}));

const MAX_BIG_TABS_COUNT = 3;
const TabbedLists = ({
  isMobile,
  isTablet,
  isDesktop,
  data,
  isFullWidth,
  assessmentName,
  topic,
  entryId,
  showTabs = true,
  grayscale,
  trackingPath = 'Assessment',
  trackingProps = {},
  clientExclusiveAsResource,
  labelValue,
}) => {
  const eventTriggered = useRef(false);
  const carouselRef = useRef();
  const [open, setOpen] = useState(false);
  const anchorEl = useRef();
  const [tab, setTab] = useState();
  const classes = useStyles({
    isDesktop,
    isTablet,
    isVariantB,
  });
  const { shouldABTest, isVariantB } = useContext(AssessmentResultContext);

  const getSlide = useCallback(
    newTab => {
      const storedLabel = sessionStorage.getItem('currentAccordion');
      const storedType = sessionStorage.getItem('currentListType');
      const storedTab = sessionStorage.getItem('currentListSlug');
      const storedSlide = sessionStorage.getItem('currentListSlide');
      if (
        storedLabel === getScoreComponentItemId(labelValue) &&
        storedType === 'Tabbed' &&
        storedTab === newTab &&
        storedSlide != null
      ) {
        sessionStorage.removeItem('currentListSlide');
        return storedSlide;
      }
      return 0;
    },
    [labelValue],
  );

  const getCurrentSlide = useCallback(() => {
    try {
      if (carouselRef.current) {
        if (carouselRef.current.activeIndex) {
          return carouselRef.current.activeIndex;
        }
        if (carouselRef.current.state.currentSlide) {
          return carouselRef.current.state.currentSlide;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }, []);

  const updateSessionTab = useCallback(
    newTab => {
      const tabSlug = sessionStorage.getItem('currentListSlug');
      if (tabSlug !== newTab) {
        sessionStorage.setItem(
          'currentAccordion',
          getScoreComponentItemId(labelValue),
        );
        sessionStorage.setItem('currentListType', 'Tabbed');
        sessionStorage.setItem('currentListSlug', newTab);
      }
    },
    [labelValue],
  );

  const onClickInterceptCallback = () => {
    sessionStorage.setItem('currentAccordionHandled', true);
    sessionStorage.setItem(
      'currentAccordion',
      getScoreComponentItemId(labelValue),
    );
    sessionStorage.setItem('currentListType', 'Tabbed');
    sessionStorage.setItem('currentListSlug', tab);
    sessionStorage.setItem('currentListSlide', getCurrentSlide());
    sessionStorage.setItem('scrollY', window.scrollY);
  };

  const tryRestore = useCallback(() => {
    const availableTabs = data.map(item => _get(item, 'fields.slug'));

    const restoreState = sessionStorage.getItem('restoreAssessmentResultState');
    const storedLabel = sessionStorage.getItem('currentAccordion');
    const storedType = sessionStorage.getItem('currentListType');
    const storedTab = sessionStorage.getItem('currentListSlug');
    if (
      restoreState &&
      storedLabel === getScoreComponentItemId(labelValue) &&
      storedType === 'Tabbed' &&
      storedTab &&
      availableTabs.includes(storedTab)
    ) {
      setTab(storedTab);
      return true;
    }
    return false;
  }, [data, labelValue]);

  useEffect(() => {
    const goTo = getSlide(tab);
    if (carouselRef.current) {
      if (carouselRef.current.slideTo) {
        carouselRef.current.slideTo(goTo, goTo === 0 ? undefined : 0);
      } else {
        carouselRef.current.goToSlide(goTo, undefined, goTo === 0);
      }
    }
  }, [tab, getSlide]);

  useEffect(() => {
    if (data.length) {
      if (!tryRestore()) {
        setTab(_get(data, '0.item.fields.slug'));
      }
    }
  }, [data, tryRestore]);

  if (!data.length) return null;

  const setRef = el => {
    carouselRef.current = el;
  };

  const [selectedList] = data.filter(
    list => _get(list, 'item.fields.slug') === tab,
  );

  const handleCarouselRightClick = () => {
    Mixpanel.track(
      'Assessment Tabbed Lists - Carousel Right Arrow',
      injectAssessmentResultsABData(
        {
          assessment: assessmentName,
          entryId,
          list: tab,
          path: trackingPath,
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const handleTabClick = value => {
    setTab(value);
    updateSessionTab(value);
    Mixpanel.track(
      'Assessment Tabbed Lists - Tab Clicked',
      injectAssessmentResultsABData(
        {
          assessment: assessmentName,
          entryId,
          list: value,
          path: trackingPath,
        },
        shouldABTest,
        isVariantB,
      ),
    );
  };

  const onVisibilityTrigger = () => {
    if (!eventTriggered.current) {
      Mixpanel.track(
        'Assessments Tabbed Lists - Visible',
        injectAssessmentResultsABData(
          {
            list: tab,
            assessment: assessmentName,
            entryId,
            path: trackingPath,
          },
          shouldABTest,
          isVariantB,
        ),
      );

      eventTriggered.current = true;
    }
  };

  const handleClickAway = e => {
    e.preventDefault();
    e.stopPropagation();

    setOpen(false);
  };

  const handleMobileTabSelect = value => {
    setTab(value);
    updateSessionTab(value);
    setOpen(false);
  };

  const getViewAllUrl = () => {
    if (_get(selectedList, 'type') === 'view-all') {
      return `/topics/${_get(topic, 'fields.slug')}/what-helps`;
    }
    if (_get(selectedList, 'type') === 'handpicked') {
      return `/topics/${_get(topic, 'fields.slug')}`;
    }
    return `/lists/${_get(selectedList, ['item', 'fields', 'slug'])}`;
  };

  const viewAllUrl = getViewAllUrl();

  const renderTabContent = (list, selected) => {
    const fallbackImage = getImageFile(
      _get(list, 'item.fields.topics[0].fields', {}),
    );
    return (
      <>
        {!isVariantB && (
          <Box className={classes.tabImageWrapper}>
            <img
              src={getImageFile(
                _get(list, 'type') === 'view-all'
                  ? _get(topic, 'fields.relatedTopics.0.fields', {})
                  : _get(list, 'item.fields', {}),
                false,
                fallbackImage,
              )}
              alt={_get(
                list,
                'item.fields.shortTitle',
                _get(list, 'item.fields.title'),
              )}
              className={_classNames({ [classes.grayscale]: grayscale })}
            />
          </Box>
        )}
        <span
          className={_classNames(classes.tabLabel, {
            [classes.tabLabelSelected]: selected,
          })}
        >
          {_get(
            list,
            'item.fields.shortTitle',
            _get(list, 'item.fields.title'),
          )}
        </span>
      </>
    );
  };

  const shouldHideTabs = isVariantB && data.length < 2;
  const showDefaultView =
    isDesktop || (isTablet && data.length < MAX_BIG_TABS_COUNT);
  const showCarouselView = isTablet && data.length >= MAX_BIG_TABS_COUNT;
  const showMobileView = isMobile && showTabs && selectedList;
  const isTabSmall = isDesktop && data.length > MAX_BIG_TABS_COUNT;

  const tabs =
    showTabs && !shouldHideTabs
      ? data.map(list => (
          <ButtonBase
            key={`tabbed-lists-tab-${_get(list, 'item.fields.slug')}`}
            disableRipple
            onClick={() => handleTabClick(_get(list, 'item.fields.slug'))}
            className={_classNames({
              [classes.tab]: true,
              selected: tab === _get(list, 'item.fields.slug'),
              small: isTabSmall,
            })}
          >
            {renderTabContent(list)}
          </ButtonBase>
        ))
      : null;

  const resources = _get(selectedList, 'resources', []);
  const finalResources = resources.length
    ? filterSelfReferencingResources(resources)
        .map((el, index) => {
          const isWhatHelps = el.parent === 'what-helps';
          const fromContentful = _has(el, 'fields');
          const finalId = isWhatHelps
            ? _get(el, 'evidenceData.approach.sys.id', el.evidenceData.id)
            : fromContentful
            ? el.sys.id
            : el.objectID;
          return (
            <ButtonBase
              className={classes.button}
              disableRipple
              onClick={onClickInterceptCallback}
            >
              <ResourceItemSelector
                key={finalId}
                item={el}
                itemIndex={index}
                grayscale={grayscale}
                trackingPath={trackingPath}
                trackingProps={trackingProps}
                clientExclusiveAsResource={clientExclusiveAsResource}
              />
            </ButtonBase>
          );
        })
        .concat(
          <ListViewAllCard
            key="list-view-all-card"
            url={viewAllUrl}
            onBeforeClick={onClickInterceptCallback}
            eventProperties={{
              page:
                trackingPath === 'Assessment'
                  ? 'assessment results'
                  : (trackingPath || '').toLowerCase(),
            }}
          />,
        )
    : [];

  const seriesIdx = resources.findIndex(
    r =>
      _get(r.fields, 'reviewStatus') === 'Accepted' &&
      _get(r.sys, 'contentType.sys.id') === 'series' &&
      _get(r, 'fields.style') === 'Wide Card',
  );
  const dynamicSlidesIndexes = seriesIdx >= 0 ? [seriesIdx] : undefined;

  return (
    <>
      {!shouldHideTabs && (
        <Grid item xs={12} className={classes.tabsWrapper}>
          {showDefaultView && tabs}
          {showCarouselView && (
            <Carousel
              slidesToSlide={2}
              small
              partialVisible
              responsive={{
                tabletLg: {
                  breakpoint: {
                    max: 1024,
                    min: 900,
                  },
                  partialVisibilityGutter: 100,
                  items: 2,
                },
                tabletMd: {
                  breakpoint: {
                    max: 900,
                    min: 700,
                  },
                  partialVisibilityGutter: 60,
                  items: 2,
                },
                tabletSm: {
                  breakpoint: {
                    max: 700,
                    min: 480,
                  },
                  items: 1,
                  partialVisibilityGutter: 200,
                },
              }}
            >
              {tabs}
            </Carousel>
          )}
          {showMobileView && (
            <>
              <ButtonBase
                ref={anchorEl}
                disableRipple
                onClick={() => setOpen(true)}
                className={_classNames({
                  [classes.tab]: true,
                  selected: true,
                })}
              >
                {renderTabContent(selectedList, true)}
                {open ? (
                  <ArrowUp
                    className={classes.mobileSelectedTabArrow}
                    alt="Arrow up"
                  />
                ) : (
                  <ArrowDown
                    className={classes.mobileSelectedTabArrow}
                    alt="Arrow down"
                  />
                )}
                <Popper
                  open={open}
                  anchorEl={anchorEl.current}
                  className={classes.popperContainer}
                  transition
                  placement="bottom"
                  disablePortal
                  style={{ position: 'absolute' }}
                  modifiers={{
                    offset: {
                      enabled: true,
                      offset: '0,6',
                    },
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      id="menu-list-grow"
                      style={{
                        transformOrigin:
                          placement === 'bottom'
                            ? 'center top'
                            : 'center bottom',
                      }}
                    >
                      <Paper className={classes.mobilePaper}>
                        <ClickAwayListener onClickAway={handleClickAway}>
                          <MenuList className={classes.mobileList}>
                            {data.map(list => (
                              <MenuItem
                                disableRipple
                                key={`tabbed-lists-mobile-tab-${_get(
                                  list,
                                  'item.fields.slug',
                                )}`}
                                className={classes.mobileListItem}
                                selected={
                                  _get(list, 'item.fields.slug') === tab
                                }
                                onClick={e => {
                                  e.stopPropagation();
                                  handleMobileTabSelect(
                                    _get(list, 'item.fields.slug'),
                                  );
                                }}
                              >
                                <ButtonBase
                                  disableTouchRipple
                                  className={_classNames({
                                    [classes.tab]: true,
                                    [classes.mobileListTab]: true,
                                    small: true,
                                  })}
                                >
                                  {renderTabContent(list)}
                                </ButtonBase>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </ButtonBase>
            </>
          )}
        </Grid>
      )}
      <Grid item xs={12} className={classes.root}>
        {!!finalResources.length && (
          <SwiperCarousel
            ref={setRef}
            bigArrows={isVariantB}
            dynamicSlidesIndexes={dynamicSlidesIndexes}
            onRightArrowClick={handleCarouselRightClick}
            breakpoints={{
              900: {
                slidesPerView: 'auto',
                slidesPerGroup: !isFullWidth || isVariantB ? 3 : 4,
                dynamicSlideWidth: 443,
              },
              480: {
                slidesPerView: 2,
                slidesPerGroup: 2,
              },
              0: {
                slidesPerView: 1,
                slidesPerGroup: 1,
              },
            }}
          >
            {finalResources}
          </SwiperCarousel>
        )}
      </Grid>
      <ScrollTrigger onTrigger={onVisibilityTrigger} />
    </>
  );
};

TabbedLists.propTypes = {
  isFullWidth: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  isDesktop: PropTypes.bool,
  grayscale: PropTypes.bool,
  data: PropTypes.array,
  assessmentName: PropTypes.string,
  topic: PropTypes.object,
  entryId: PropTypes.string,
  labelValue: PropTypes.string,
  trackingPath: PropTypes.string,
  trackingProps: PropTypes.object,
  isVariantB: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width <= 480,
  isTablet: width > 480 && width <= 1024,
  isDesktop: width > 1024,
});

export default withSizes(mapSizesToProps)(TabbedLists);
